import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, CheckCircle2, Loader2, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScheduleModal = ({ isOpen, onClose, source, leadId = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    phone: '',
    preferred_time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const preferredTimeOptions = [
    'Morning (9am - 12pm)',
    'Afternoon (12pm - 5pm)',
    'Evening (5pm - 8pm)',
    'Flexible / Any time'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/events/schedule-interest`, {
        ...formData,
        source: source || 'schedule_modal',
        lead_id: leadId,
        event_type: 'schedule_interest'
      });

      setShowSuccess(true);
      
      if (onSuccess) {
        onSuccess(formData);
      }

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        // Reset form
        setFormData({
          first_name: '',
          email: '',
          phone: '',
          preferred_time: '',
          message: ''
        });
      }, 3500);
    } catch (error) {
      // Error handled by UI state
      alert('Failed to submit. Please try again or contact Bryan directly.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg bg-slate-950/95 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-[0_0_50px_rgba(34,211,238,0.3)] overflow-hidden"
          data-testid="schedule-modal"
        >
          {showSuccess ? (
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 flex items-center justify-center"
              >
                <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              </motion.div>
              <h3 className="font-heading text-2xl font-bold mb-3 text-white">Request Received!</h3>
              <p className="text-slate-300 text-lg mb-2">
                Thanks — Bryan will review your forecast and reach out shortly.
              </p>
              <p className="text-slate-500 text-sm">
                Typically within a few hours during business hours
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/10 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-[#22D3EE]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-white">Schedule a Call</h3>
                    <p className="text-sm text-cyan-400">Bryan will reach out at your preferred time</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="schedule-modal-close"
                >
                  <X className="h-5 w-5 text-slate-400 hover:text-white" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* First Name */}
                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">First Name *</Label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Your first name"
                    className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-12 text-white placeholder-slate-500"
                    required
                    data-testid="schedule-name"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 text-sm mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your@email.com"
                      className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-12 text-white placeholder-slate-500"
                      required
                      data-testid="schedule-email"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 text-sm mb-2 block">Phone Number *</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="bg-slate-900/60 border-slate-800 focus:border-cyan-500 h-12 text-white placeholder-slate-500"
                      required
                      data-testid="schedule-phone"
                    />
                  </div>
                </div>

                {/* Preferred Callback Time */}
                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">
                    <Clock className="inline h-4 w-4 mr-1" />
                    Preferred Callback Time *
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {preferredTimeOptions.map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setFormData({...formData, preferred_time: time})}
                        className={`py-3 px-4 rounded-lg border text-sm transition-all ${
                          formData.preferred_time === time
                            ? 'border-cyan-500 bg-cyan-500/10 text-cyan-300'
                            : 'border-slate-800 bg-slate-900/30 text-slate-400 hover:border-slate-700'
                        }`}
                        data-testid={`schedule-time-${time.toLowerCase().replace(/[^a-z]/g, '-')}`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional Message */}
                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">Message (Optional)</Label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Any specific questions or details you'd like to discuss..."
                    rows={3}
                    className="w-full bg-slate-900/60 border border-slate-800 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 rounded-lg p-3 text-sm text-white placeholder-slate-500 resize-none"
                    data-testid="schedule-message"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.preferred_time}
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white rounded-xl py-6 text-base font-bold h-auto shadow-[0_0_20px_rgba(16,185,129,0.4)] hover:shadow-[0_0_30px_rgba(16,185,129,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="schedule-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Calendar className="mr-2 h-5 w-5" />
                      Request Callback
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  Bryan typically calls back within a few hours during business hours
                </p>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ScheduleModal;
